import React, { useCallback, useMemo } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import FormHelperText from '@material-ui/core/FormHelperText';
import Typography from '@material-ui/core/Typography';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { IFormOption } from '../../_core/_ui/forms.component';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { StageSubmitRegistrar } from '../../_core/Formik/formik-submit-registrar.component';
import { DiagramUploadRow } from '../diagram-upload-row.component';
import { FormatTemplatePills } from '../format-template-pills.component';
import { FormatTemplateStageList } from '../format-template-stage-list.component';
import { messages } from '../format-template.messages';

interface IEditCatalogTemplateModalProps {
  open: boolean;
  onClose: () => void;
  template: IFormatTemplate;
  stageLibrary: IFormatTemplateStageDefinition[];
  stages: IAssessmentStage[];
  updateStage: (id: string, changes: { config?: object; title?: string }) => void;
  registerStageSubmit: StageSubmitRegistrar;
  drillOptions: IFormOption[];
  practicePlanOptions: IFormOption[];
  positionOptions: IFormOption[];
  stagesLoading: boolean;
  pendingDiagrams: { [bandKey: string]: { imagePath: string; previewUrl: string } };
  uploadingBandKey: string | null;
  onSelectDiagram: (bandKey: string, file: File) => void;
  onSave: () => void;
  saving: boolean;
  error: React.ReactNode;
}

export const EditCatalogTemplateModal: React.FC<IEditCatalogTemplateModalProps> = ({
  open,
  onClose,
  template,
  stageLibrary,
  stages,
  updateStage,
  registerStageSubmit,
  drillOptions,
  practicePlanOptions,
  positionOptions,
  stagesLoading,
  pendingDiagrams,
  uploadingBandKey,
  onSelectDiagram,
  onSave,
  saving,
  error,
}) => {
  const bands = useMemo(() => template.bands ?? [], [template]);
  const bandLabelByKey = useMemo(
    () => new Map(bands.map(band => [band.key, band.label || band.key])),
    [bands],
  );
  const definitionByKey = useMemo(
    () => new Map(stageLibrary.map(definition => [definition.key, definition])),
    [stageLibrary],
  );

  // A setting edit reaches every band running the stage — the chips show that reach up front.
  // A divider above each section (bar the first) keeps it visually apart from the previous config.
  const renderStageAnnotation = useCallback((stage: IAssessmentStage, index: number) => {
    const definition = definitionByKey.get(String(stage._id));
    if (!definition || !bands.length) return null;
    return (
      <>
        {index > 0 && <Box mt={3}><Divider /></Box>}
        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">
            <IntlMsg
              {...messages.bandReach}
              values={{
                count: definition.bandKeys.length,
                total: bands.length,
                bandWord: pluralize(bands.length, 'band'),
              }}
            />
          </Typography>
          <Box display="flex" flexWrap="wrap" mt={0.5} style={{ gap: 4 }}>
            {definition.bandKeys.map(key => (
              <Chip key={key} label={bandLabelByKey.get(key) ?? key} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      </>
    );
  }, [definitionByKey, bands.length, bandLabelByKey]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle><IntlMsg {...messages.editCatalogTitle} /></DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1">{template.name}</Typography>
        <Box mt={0.5}>
          <FormatTemplatePills template={template} showAgeGroups />
        </Box>
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">
            <IntlMsg {...messages.catalogEditHint} />
          </Typography>
        </Box>

        <Box my={2}><Divider /></Box>

        <Typography variant="subtitle2"><IntlMsg {...messages.catalogStageSettingsTitle} /></Typography>
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
          <IntlMsg {...messages.catalogStagesEditHint} />
        </Typography>
        {stagesLoading && stages.length === 0 ? (
          <Box display="flex" alignItems="center" mt={1}>
            <CircularProgress size={16} style={{ marginRight: 8 }} />
            <Typography variant="caption" color="textSecondary"><IntlMsg {...messages.loadingStageSettings} /></Typography>
          </Box>
        ) : (
          <FormatTemplateStageList
            stages={stages}
            drillOptions={drillOptions}
            practicePlanOptions={practicePlanOptions}
            positionOptions={positionOptions}
            loading={stagesLoading}
            editable
            updateStage={updateStage}
            catalogSettingsMode
            renderStageAnnotation={renderStageAnnotation}
            registerStageSubmit={registerStageSubmit}
          />
        )}

        {bands.length > 0 && (
          <>
            <Box my={2}><Divider /></Box>
            <Typography variant="subtitle2"><IntlMsg {...messages.bandDiagramsTitle} /></Typography>
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              <IntlMsg {...messages.bandDiagramsHelp} />
            </Typography>
            {bands.map(band => (
              <DiagramUploadRow
                key={band.key}
                label={band.label || band.key}
                alt={band.label || band.key}
                imageUrl={band.imageUrl}
                pendingPreviewUrl={pendingDiagrams[band.key]?.previewUrl}
                uploading={uploadingBandKey === band.key}
                disabled={saving || (!!uploadingBandKey && uploadingBandKey !== band.key)}
                onSelectFile={file => onSelectDiagram(band.key, file)}
              />
            ))}
          </>
        )}

        {error && (
          <Box mt={1}>
            <FormHelperText error>{error}</FormHelperText>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}><IntlMsg {...messages.cancel} /></Button>
        <Button onClick={onSave} color="primary" variant="contained" disabled={saving || !!uploadingBandKey}>
          {saving ? (
            <>
              <CircularProgress size={18} style={{ marginRight: 8 }} />
              <IntlMsg {...messages.saving} />
            </>
          ) : <IntlMsg {...messages.save} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
